import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Iframe from 'react-iframe';
import {M3D_SCENES_BASE_URL} from 'utils/World/config';
import {WEB_SCENES_VERSION} from 'config/app.config';
import getRemoteConfigValue from 'utils/firebase/remoteConfig';
import {resolveWebSceneUrl} from 'utils/World/webSceneResolver';

class PlatformWorld extends Component {
  static instance = undefined;

  constructor(props) {
    super(props);
    const swSupported =
      typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
    const hasController =
      swSupported && Boolean(navigator.serviceWorker.controller);

    // If SW is supported but not yet controlling the page, delay initial Unity load
    // to avoid the first-session "download twice" problem.
    this.state = {
      allowUnityLoad: !swSupported || hasController,
      sceneUrl: null,
      sceneLocation: null,
    };
  }

  componentDidMount() {
    PlatformWorld.instance = this;
    window.addEventListener('message', PlatformWorld.onWindowMessage);
    this.applyIframeStyling();
    this.maybeGateUnityLoadOnServiceWorker();
    this.resolveSceneUrl();
  }

  componentWillUnmount() {
    this.sceneResolutionRequestId = (this.sceneResolutionRequestId || 0) + 1;
    PlatformWorld.instance = undefined;
    window.removeEventListener('message', PlatformWorld.onWindowMessage);
    this.cleanupServiceWorkerListeners?.();
  }

  componentDidUpdate(previousProps, previousState) {
    this.applyIframeStyling();
    if (
      previousProps.location !== this.props.location ||
      (!previousState.allowUnityLoad && this.state.allowUnityLoad)
    ) {
      this.resolveSceneUrl();
    }
  }

  resolveSceneUrl = async () => {
    const requestId = (this.sceneResolutionRequestId || 0) + 1;
    this.sceneResolutionRequestId = requestId;

    let remoteVersion = '';
    try {
      remoteVersion = getRemoteConfigValue('web_scenes_version')?.asString();
    } catch (_) {
      // The source-pinned release is the safe fallback when Remote Config is unavailable.
    }

    const sceneUrl = await resolveWebSceneUrl({
      baseUrl: M3D_SCENES_BASE_URL,
      remoteVersion,
      fallbackVersion: WEB_SCENES_VERSION,
      location: this.props.location,
    });

    if (requestId === this.sceneResolutionRequestId) {
      this.setState({sceneUrl, sceneLocation: this.props.location});
    }
  };

  applyIframeStyling() {
    const iframe = document.getElementById('world-iframe');
    if (!iframe) return;
    this.iframe = iframe;
    this.iframe.style.border = 'none';
  }

  maybeGateUnityLoadOnServiceWorker = async () => {
    try {
      if (!('serviceWorker' in navigator)) return;
      if (navigator.serviceWorker.controller) return;
      if (this.state.allowUnityLoad === true) {
        // Flip to blocked immediately so we don't start Unity load before SW controls.
        this.setState({allowUnityLoad: false});
      }

      const start = Date.now();

      const waitForController = () =>
        new Promise((resolve) => {
          const onControllerChange = () => resolve();
          navigator.serviceWorker.addEventListener(
            'controllerchange',
            onControllerChange,
          );

          this.cleanupServiceWorkerListeners = () => {
            navigator.serviceWorker.removeEventListener(
              'controllerchange',
              onControllerChange,
            );
          };

          // If SW is already installed/active, this resolves when it's ready.
          // (controller may still be null until activate/clientsClaim)
          navigator.serviceWorker.ready
            .then(() => {
              if (navigator.serviceWorker.controller) resolve();
            })
            .catch(() => {});
        });

      const timeoutMs = 3500;
      await Promise.race([
        waitForController(),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]);

      const waitedMs = Date.now() - start;
      console.log('[SW] Unity load gate released', {
        waitedMs,
        controller: Boolean(navigator.serviceWorker.controller),
      });

      this.setState({allowUnityLoad: true});
    } catch (e) {
      // Never block world loading due to SW issues.
      this.setState({allowUnityLoad: true});
    }
  };

  static onWindowMessage(event) {
    const {instance} = PlatformWorld;
    if (!instance) return;

    try {
      if (event.data) {
        const {action, route} = event.data;
        if (action || route) {
          instance.onMessageFromWorld(event.data);
        }
      }
    } catch (e) {
      console.error('onWindowMessage() | failed:', e);
    }
  }

  onMessageFromWorld = message => {
    const {onTransform, onLegacyMessage, onCommand} = this.props;
    if (message.command) onCommand(message.command);
    if (message.route) {
      message.route === 'j' ? onTransform(message) : onLegacyMessage(message);
    }
  };

  static sendMessage = message => {
    const {instance} = PlatformWorld;
    if (!instance || !instance.iframe?.contentWindow) return;
    instance.iframe.contentWindow.postMessage(message, '*');
  };

  render() {
    const iframeUrl =
      this.state.allowUnityLoad &&
      this.state.sceneUrl &&
      this.state.sceneLocation === this.props.location
        ? this.state.sceneUrl
        : 'about:blank';

    return (
      <Iframe
        rel="preload"
        url={iframeUrl}
        width="100%"
        height="100%"
        id="world-iframe"
        className="iframe-3dmeet"
        display="initial"
        position="absolute"
        scrolling="no"
        name={this.props.location}
        aria-label={this.props.location}
      />
    );
  }
}

PlatformWorld.defaultProps = {
  onTransform: () => {},
  onLegacyMessage: () => {},
  onCommand: () => {},
  onAppearanceComplete: () => {},
};

PlatformWorld.propTypes = {
  location: PropTypes.string.isRequired,
  onTransform: PropTypes.func,
  onLegacyMessage: PropTypes.func,
  onCommand: PropTypes.func,
  onAppearanceComplete: PropTypes.func,
};

export default PlatformWorld;
